import {
  waitForRenderThenFocus,
  focusElement,
} from 'platform/utilities/ui/focus';
import { scrollTo } from 'platform/utilities/scroll';

import { isMinimalHeaderApp } from '../patterns/minimal-header';

/**
 * Moves focus (and scroll) on arrival at the review-and-submit page so keyboard
 * and screen-reader users land in the right place.
 *
 * No-ops off the review page, mid-interaction with an accordion header or
 * va-select, and when returning from a review-page edit.
 *
 * @param {Object} params
 * @param {string} [params.pathname] - Active pathname; used to detect the review page.
 * @param {string} [params.currentChapterKey] - Current page's chapter key ('review' on the review page).
 * @param {Object} [params.locationState] - Router `location.state`; carries edit-return signals.
 * @param {boolean} [params.hideFormNavProgress=false] - Whether the current chapter hides progress.
 * @param {string} [params.landingTarget='#nav-form-header'] - Element focused on a fresh review landing.
 * @param {string} [params.landingChildSelector] - Optional child selector focused inside `landingTarget`.
 * @param {Function} [params.focusAfterRender=waitForRenderThenFocus] - Injectable render-then-focus (callers can pass a test double).
 */
export function focusReviewPageOnArrival({
  pathname,
  currentChapterKey,
  locationState,
  hideFormNavProgress = false,
  landingTarget = '#nav-form-header',
  landingChildSelector,
  focusAfterRender = waitForRenderThenFocus,
} = {}) {
  const { activeElement } = document;
  const isVaSelect =
    activeElement?.tagName === 'VA-SELECT' ||
    activeElement?.closest('va-select') ||
    activeElement?.classList.contains('rjsf-web-component-field');

  // Don't steal focus while the user is interacting with an accordion header
  // or a va-select.
  if (activeElement?.closest('.accordion-header') || isVaSelect) {
    return;
  }

  const onReviewPage =
    currentChapterKey === 'review' || pathname?.endsWith('review-and-submit');
  if (!onReviewPage) {
    return;
  }

  const { chapterKey, reviewEditReturnPageKey, reviewEditSuccess } =
    locationState || {};

  if (reviewEditReturnPageKey || reviewEditSuccess) {
    return;
  }

  // Returned to review after editing a chapter: focus that chapter's accordion.
  if (chapterKey) {
    const accordion = document.querySelector(
      `va-accordion-item[data-chapter="${chapterKey}"]`,
    );
    if (accordion) {
      const selector = isMinimalHeaderApp() ? 'h2' : 'h3';
      focusElement(selector, {}, accordion);
      // Scroll after focus has completed.
      requestAnimationFrame(() =>
        scrollTo(`chapter${chapterKey}ScrollElement`),
      );
    }
    return;
  }

  // Fresh landing on the review page.
  if (!hideFormNavProgress && !isMinimalHeaderApp()) {
    scrollTo('topScrollElement');
  }
  if (hideFormNavProgress || isMinimalHeaderApp()) {
    focusElement('h1');
  } else {
    focusAfterRender(landingTarget, document, 250, landingChildSelector);
  }
}
